import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InlineChat from "../components/InlineChat";
import "../styles/animations.css";

function Home() {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const tiles = [
    {
      to: "/resume",
      label: "Resume",
      description: "Experience, skills, and the full résumé.",
      cta: "View Resume",
      delay: 0,
      image: "/assets/tiles/resume.jpg",
      // Headshot — keep the face visible by cropping to the top half
      objectPosition: "center 20%",
    },
    {
      to: "/av-consulting",
      label: "AV and Home Automation Consulting",
      description: "Enterprise AV and smart-home automation design, programming, and integration.",
      cta: "View portfolio",
      delay: 100,
      image: "/assets/tiles/av-consulting.jpg",
      objectPosition: "center 30%",
    },
    {
      to: "/passions",
      label: "Passions & Hobbies",
      description: "Life outside of work — the outdoors, sports, and making music.",
      cta: "Explore passions",
      delay: 200,
      image: "/assets/tiles/passions.jpg",
      objectPosition: "center center",
    },
    {
      to: "/archive",
      label: "Archive",
      description: "Earlier versions of this site through the years.",
      cta: "Browse archive",
      delay: 300,
      image: "/assets/tiles/archive.jpg",
    },
  ];

  // Hard navigations + fadeOut forwards can leave Home at opacity 0 in bfcache.
  useEffect(() => {
    const resetNavigationState = () => {
      setIsNavigating(false);
      setSelectedIndex(null);
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) resetNavigationState();
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const getDriftDirection = (clickedIndex: number, tileIndex: number) => {
    if (clickedIndex === tileIndex) return null;

    const cols = typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1;
    const clickedRow = Math.floor(clickedIndex / cols);
    const tileRow = Math.floor(tileIndex / cols);

    if (clickedRow < tileRow) return "driftDown";
    if (clickedRow > tileRow) return "driftUp";
    if (clickedIndex < tileIndex) return "driftRight";
    return "driftLeft";
  };

  const handleNavigate = (index: number, path: string) => {
    setSelectedIndex(index);
    setIsNavigating(true);

    setTimeout(() => {
      navigate(path);
    }, 300);
  };

  return (
    <main
      className="flex-1 w-full px-4 md:px-8 pt-6 pb-12 max-w-7xl mx-auto"
      style={{
        animation: isNavigating ? "fadeOut 0.4s ease-out forwards" : "fadeIn 0.6s ease-out",
      }}
    >
      <InlineChat />

      {/* Two-column tile nav — description + dedicated CTA per item */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {tiles.map((tile, idx) => {
          const driftDirection =
            selectedIndex !== null ? getDriftDirection(selectedIndex, idx) : null;
          const animationStyle =
            isNavigating && driftDirection
              ? `${driftDirection} 1.5s ease-out forwards`
              : `slideInUp 1.6s ease-out ${tile.delay}ms backwards`;

          return (
            <article
              key={tile.to}
              className="tile glass-tile group relative flex min-h-[148px] overflow-hidden transition-all duration-300 hover:scale-[1.015]"
              style={{
                animation: animationStyle,
              }}
            >
              <img
                src={tile.image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-35 pointer-events-none"
                style={
                  "objectPosition" in tile && tile.objectPosition
                    ? { objectPosition: tile.objectPosition }
                    : undefined
                }
              />
              <div className="absolute inset-0 bg-black/50 pointer-events-none" />

              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/10 group-hover:via-purple-500/10 group-hover:to-pink-500/10 transition-all duration-300 pointer-events-none" />

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-shimmer" />
              </div>

              <div className="relative z-10 flex w-full items-center justify-between gap-4 px-8 py-6 sm:px-10 sm:pr-12">
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-bold drop-shadow transition-colors duration-300 group-hover:text-blue-300">
                    {tile.label}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-300">
                    {tile.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNavigate(idx, tile.to)}
                  className="btn-teal btn-sm shrink-0"
                  disabled={isNavigating}
                >
                  {tile.cta}
                  <span aria-hidden="true" className="ml-1.5">
                    →
                  </span>
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-12 text-center text-gray-500 text-sm">
        <p>© {new Date().getFullYear()} Walt Yao. All rights reserved.</p>
      </div>
    </main>
  );
}

export default Home;
